<template>
    <TailwindLayout title="Enterprise Application">
        <div class="bg-white rounded-lg shadow-sm -mx -mt-10">
            <div class="px-6 pt-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Link :href="route('bridge.enterprise-applications.index')" class="text-sm text-rent-risk-blue-400 hover:underline">&larr; All applications</Link>
                    <h1 class="font-semibold text-2xl leading-tight text-gray-900 mt-1">{{ application.legal_entity_name }}</h1>
                    <p class="text-sm text-gray-600 mt-1">{{ businessTypeLabel }} — submitted {{ submittedAt || '—' }}</p>
                </div>
                <div class="flex items-center gap-2">
                    <a :href="pdfUrl" class="px-4 py-2 text-sm font-semibold text-white bg-rent-risk-blue-400 rounded-md hover:opacity-90">Download PDF</a>
                </div>
            </div>

            <div class="px-6 py-6 space-y-8">
                <!-- Review decision -->
                <section class="bg-gray-50 border border-gray-200 rounded-md px-4 py-4 space-y-5">
                    <div class="flex flex-wrap items-center justify-between gap-2">
                        <h2 class="text-sm font-semibold text-gray-800">Reviewer decision</h2>
                        <span class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium" :class="badge(application.status)">{{ titleCase(application.status) }}</span>
                    </div>

                    <p v-if="review.reviewer" class="text-xs text-gray-500">
                        Last reviewed by {{ review.reviewer.name }} ({{ review.reviewer.email }}) on {{ review.reviewed_at || '—' }}.
                    </p>

                    <!-- Site inspection checklist -->
                    <fieldset>
                        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Site inspection review</legend>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                            <label v-for="check in siteInspectionChecks" :key="check" class="flex items-center gap-2 text-sm text-gray-700">
                                <input type="checkbox" v-model="form.site_inspection_review[check]" class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                                {{ humanize(check) }}
                            </label>
                        </div>
                    </fieldset>

                    <!-- Security controls checklist -->
                    <fieldset>
                        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Security controls review</legend>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                            <label v-for="check in securityControlChecks" :key="check" class="flex items-center gap-2 text-sm text-gray-700">
                                <input type="checkbox" v-model="form.security_controls_review[check]" class="rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                                {{ humanize(check) }}
                            </label>
                        </div>
                    </fieldset>

                    <!-- Customer Alert List check -->
                    <!-- TODO(RAC-10285): confirm whether Experian offers a Customer Alert List API vs manual portal lookup; for now this is a manual reviewer-recorded result. -->
                    <fieldset>
                        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Customer Alert List check</legend>
                        <div class="flex flex-wrap gap-4">
                            <label v-for="r in calResults" :key="r" class="flex items-center gap-2 text-sm text-gray-700">
                                <input type="radio" :value="r" v-model="form.customer_alert_list_result" class="border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                                {{ humanize(r) }}
                            </label>
                        </div>
                        <p v-if="form.errors.customer_alert_list_result" class="text-xs text-red-600 mt-1">{{ form.errors.customer_alert_list_result }}</p>
                        <textarea v-model="form.customer_alert_list_notes" rows="2" placeholder="Customer Alert List notes (optional)" class="mt-2 w-full rounded-md border-gray-300 shadow-sm sm:text-sm border" />
                    </fieldset>

                    <!-- Trade.gov Consolidated Screening List check (EUIR §1.11) -->
                    <!-- Manual reviewer-recorded result — check the company name and each contact at https://www.trade.gov/consolidated-screening-list -->
                    <fieldset>
                        <legend class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Trade.gov Consolidated Screening List check</legend>
                        <div class="flex flex-wrap gap-4">
                            <label v-for="r in cslResults" :key="r" class="flex items-center gap-2 text-sm text-gray-700">
                                <input type="radio" :value="r" v-model="form.consolidated_screening_list_result" class="border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400" />
                                {{ humanize(r) }}
                            </label>
                        </div>
                        <p v-if="form.errors.consolidated_screening_list_result" class="text-xs text-red-600 mt-1">{{ form.errors.consolidated_screening_list_result }}</p>
                        <textarea v-model="form.consolidated_screening_list_notes" rows="2" placeholder="Consolidated Screening List notes (optional)" class="mt-2 w-full rounded-md border-gray-300 shadow-sm sm:text-sm border" />
                    </fieldset>

                    <!-- Reviewer note (required to deny / request more info) -->
                    <div>
                        <label class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Reviewer note <span class="normal-case font-normal text-gray-400">(required to deny or request more info — shared with the applicant)</span></label>
                        <textarea v-model="form.review_notes" rows="3" class="w-full rounded-md border-gray-300 shadow-sm sm:text-sm border" />
                        <p v-if="form.errors.review_notes" class="text-xs text-red-600 mt-1">{{ form.errors.review_notes }}</p>
                    </div>

                    <div class="flex flex-wrap items-center gap-3">
                        <button type="button" @click="submitReview('approve')" :disabled="form.processing" class="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-md hover:opacity-90 disabled:opacity-50">Approve</button>
                        <button type="button" @click="submitReview('deny')" :disabled="form.processing" class="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-md hover:opacity-90 disabled:opacity-50">Deny</button>
                        <button type="button" @click="submitReview('request_info')" :disabled="form.processing" class="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">Request more info</button>
                        <span v-if="$page.props.flash?.success" class="text-sm text-green-600">{{ $page.props.flash.success }}</span>
                    </div>
                </section>

                <!-- Flags -->
                <section v-if="application.has_sanctioned_owner" class="bg-red-50 border border-red-200 text-red-800 text-sm rounded-md px-4 py-3">
                    Flagged: applicant reported an owner located in a sanctioned country.
                </section>

                <!-- Business identity -->
                <section>
                    <h2 class="text-sm font-semibold text-gray-800 border-b pb-2 mb-3">Business identity</h2>
                    <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                        <Row label="Legal entity name" :value="application.legal_entity_name" />
                        <Row label="Business type" :value="businessTypeLabel" />
                        <Row label="DBAs" :value="(application.dbas || []).filter(Boolean).join(', ')" />
                        <Row label="Physical address" :value="application.physical_address" />
                        <Row label="Phone" :value="application.phone" />
                        <Row label="Company website" :value="application.company_website" />
                        <Row label="Company email" :value="application.company_email" />
                        <Row label="# Property managers" :value="application.num_property_managers" />
                        <Row label="Time in business" :value="application.time_in_business" />
                        <Row label="Ownership type" :value="ownershipLabel" />
                        <Row label="Tax ID" :value="application.tax_id" />
                        <Row label="SoS registration #" :value="application.sos_registration_number" />
                        <Row label="SoS registration state" :value="application.sos_registration_state" />
                        <Row label="PM license #" :value="application.pm_license_number || '—'" />
                        <Row label="PM license state" :value="application.pm_license_state || '—'" />
                        <Row label="Permissible purpose" :value="(application.permissible_purpose || '').replace('_', ' ')" />
                        <Row label="Permissible purpose certified" :value="application.permissible_purpose_certified ? 'Yes' : 'No'" />
                        <Row label="Account holder" :value="contact ? `${contact.name} (${contact.email})` : '—'" />
                        <Row label="Certified not ineligible" :value="application.certified_not_ineligible ? 'Yes' : 'No'" />
                        <Row label="Terms accepted (version)" :value="tosConsentLabel" />
                    </dl>
                    <p v-if="application.business_type_description" class="text-sm text-gray-600 mt-3">{{ application.business_type_description }}</p>
                </section>

                <!-- Principals + agents -->
                <section>
                    <h2 class="text-sm font-semibold text-gray-800 border-b pb-2 mb-3">Principals / Owners</h2>
                    <ul class="text-sm text-gray-700 list-disc pl-5">
                        <li v-for="(p, i) in application.principals || []" :key="i">{{ p.name }}<span v-if="p.title"> — {{ p.title }}</span></li>
                        <li v-if="!(application.principals || []).length" class="list-none text-gray-400">None listed</li>
                    </ul>
                    <p class="text-sm text-gray-600 mt-3"><span class="font-medium">Technical providers / agents:</span> {{ (application.agents || []).filter(Boolean).join(', ') || 'None reported' }}</p>
                </section>

                <!-- Responsible owner / officer -->
                <section v-if="application.responsible_officer">
                    <h2 class="text-sm font-semibold text-gray-800 border-b pb-2 mb-3">Responsible owner / officer</h2>
                    <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                        <Row label="Name" :value="application.responsible_officer.name" />
                        <Row label="Title" :value="application.responsible_officer.title" />
                        <Row label="Ownership %" :value="application.responsible_officer.ownership_percentage ?? '—'" />
                        <Row label="Email" :value="application.responsible_officer.email" />
                        <Row label="Phone" :value="application.responsible_officer.phone || '—'" />
                    </dl>
                </section>

                <!-- Security controls (RAC-10282) -->
                <section>
                    <h2 class="text-sm font-semibold text-gray-800 border-b pb-2 mb-3">Security controls questionnaire</h2>
                    <div v-if="securityQuestions.length" class="space-y-4">
                        <div v-for="group in securityQuestions" :key="group.section">
                            <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{{ group.section }}</p>
                            <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                <Row v-for="q in group.questions" :key="q.key" :label="q.label" :value="answerLabel(securityAnswers[q.key])" />
                                <template v-for="q in group.questions" :key="q.key + '-detail'">
                                    <Row v-if="q.detail_key && securityAnswers[q.detail_key]" :label="q.detail_label || 'Details'" :value="securityAnswers[q.detail_key]" />
                                </template>
                            </dl>
                        </div>
                    </div>
                    <p v-else class="text-gray-400 text-sm">No security questionnaire on file.</p>
                </section>

                <!-- Documents -->
                <section>
                    <h2 class="text-sm font-semibold text-gray-800 border-b pb-2 mb-3">Uploaded documents</h2>
                    <ul class="space-y-1 text-sm">
                        <li v-for="doc in documents" :key="doc.id" class="flex items-center gap-2">
                            <span class="text-gray-500 w-44 shrink-0">{{ doc.kind }}</span>
                            <a :href="doc.download_url" class="text-rent-risk-blue-400 hover:underline">{{ doc.original_filename }}</a>
                        </li>
                        <li v-if="!documents.length" class="text-gray-400">No documents uploaded.</li>
                    </ul>
                </section>

                <!-- Site inspection (Appendix 1) -->
                <section v-if="siteInspectionFields.length || sitePhotos.length">
                    <h2 class="text-sm font-semibold text-gray-800 border-b pb-2 mb-3">Site inspection (Appendix 1)</h2>

                    <div v-for="group in siteInspectionGroups" :key="group.section" class="mb-4">
                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{{ group.section }}</p>
                        <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                            <Row
                                v-for="field in group.fields"
                                :key="field.key"
                                :label="field.label"
                                :value="siteInspectionValue(field)"
                            />
                        </dl>
                    </div>

                    <div v-if="siteInspectionAck" class="mb-4 border border-gray-200 rounded-md p-3">
                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Appendix 1 acknowledgment</p>
                        <p class="text-xs text-gray-600">{{ siteInspectionAcknowledgment?.body }}</p>
                        <p class="text-xs text-gray-700 mt-2">
                            {{ siteInspectionAck.accepted ? 'Acknowledged' : 'Not acknowledged' }}
                            by {{ siteInspectionAck.name || '—' }}<span v-if="siteInspectionAck.title">, {{ siteInspectionAck.title }}</span>
                            <span v-if="siteInspectionAck.acknowledged_at"> on {{ siteInspectionAck.acknowledged_at }}</span>
                        </p>
                    </div>

                    <div v-if="sitePhotos.length" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div v-for="photo in sitePhotos" :key="photo.id" class="border border-gray-200 rounded-md p-3">
                            <p class="text-sm font-medium text-gray-800">{{ photo.kind }}</p>
                            <img v-if="photo.preview_url" :src="photo.preview_url" :alt="photo.original_filename" class="mt-2 w-full h-40 object-cover rounded border border-gray-200" />
                            <p v-else class="mt-2 text-xs text-gray-500">{{ photo.original_filename }} (preview unavailable)</p>
                            <p v-if="photo.has_geolocation" class="text-xs text-gray-600 mt-2">
                                Location: {{ photo.gps_lat }}, {{ photo.gps_lng }}
                                <span v-if="photo.captured_at"> — captured {{ photo.captured_at }}</span>
                            </p>
                            <p v-else class="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-2 inline-block">
                                No geolocation in photo metadata
                            </p>
                        </div>
                    </div>
                    <p v-else class="text-gray-400 text-sm">No site photos uploaded.</p>
                </section>

                <!-- Signatures -->
                <section>
                    <h2 class="text-sm font-semibold text-gray-800 border-b pb-2 mb-3">Signed acknowledgments</h2>
                    <div class="space-y-4">
                        <div v-for="sig in signatures" :key="sig.kind" class="border border-gray-200 rounded-md p-3">
                            <p class="text-sm font-medium text-gray-800">{{ sig.title }}</p>
                            <img v-if="sig.signature_base64" :src="sig.signature_base64" alt="signature" class="h-16 border border-gray-200 mt-2" />
                            <p class="text-xs text-gray-500 mt-1">
                                Signed by {{ sig.signer_name }}<span v-if="sig.signer_title">, {{ sig.signer_title }}</span>
                                on {{ sig.signed_at }} (IP {{ sig.signed_ip || 'n/a' }})
                            </p>
                        </div>
                        <p v-if="!signatures.length" class="text-gray-400 text-sm">No signatures captured.</p>
                    </div>
                </section>
            </div>
        </div>
    </TailwindLayout>
</template>

<script setup>
import { computed } from 'vue'
import { Link, useForm } from '@inertiajs/vue3'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import Row from '@/Pages/Enterprise/Partials/ReviewRow.vue'

const props = defineProps({
    application: { type: Object, required: true },
    businessTypeLabel: { type: String, default: '' },
    ownershipLabel: { type: String, default: '' },
    submittedAt: { type: String, default: '' },
    contact: { type: Object, default: null },
    documents: { type: Array, default: () => [] },
    signatures: { type: Array, default: () => [] },
    sitePhotos: { type: Array, default: () => [] },
    siteInspection: { type: Object, default: () => ({}) },
    siteInspectionFields: { type: Array, default: () => [] },
    siteInspectionAcknowledgment: { type: Object, default: () => ({}) },
    statuses: { type: Array, default: () => [] },
    review: { type: Object, default: () => ({}) },
    siteInspectionChecks: { type: Array, default: () => [] },
    securityControlChecks: { type: Array, default: () => [] },
    calResults: { type: Array, default: () => [] },
    cslResults: { type: Array, default: () => [] },
    pdfUrl: { type: String, required: true },
    securityQuestions: { type: Array, default: () => [] }, // RAC-10282
})

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : '—')
const humanize = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// RAC-10282 — render questionnaire booleans as Yes/No; null/undefined = unanswered.
const securityAnswers = props.application.security_answers || {}
const answerLabel = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—')

const siteInspectionValue = (field) => {
    const raw = props.siteInspection?.[field.key]
    if (field.type === 'boolean') {
        if (raw === null || raw === undefined || raw === '') { return '—' }
        return raw ? 'Yes' : 'No'
    }
    return raw === null || raw === undefined || raw === '' ? '—' : raw
}

// Appendix 1 checklist fields grouped by their config `section` for display.
const siteInspectionGroups = computed(() => {
    const groups = []
    for (const field of props.siteInspectionFields || []) {
        let group = groups.find((g) => g.section === field.section)
        if (!group) { group = { section: field.section, fields: [] }; groups.push(group) }
        group.fields.push(field)
    }
    return groups
})

const siteInspectionAck = computed(() => props.siteInspection?.acknowledgment || null)

// RAC-10346 — versioned ToS consent captured at signup.
const tosConsentLabel = computed(() => {
    const version = props.application.tos_version
    if (!version) { return '—' }
    const acceptedAt = props.application.tos_accepted_at
    return acceptedAt ? `${version} (accepted ${new Date(acceptedAt).toLocaleDateString()})` : version
})

const badge = (status) => ({
    submitted: 'bg-blue-100 text-blue-800',
    forwarded: 'bg-amber-100 text-amber-800',
    more_info: 'bg-purple-100 text-purple-800',
    approved: 'bg-green-100 text-green-800',
    declined: 'bg-red-100 text-red-800',
}[status] || 'bg-gray-100 text-gray-700')

const seedChecklist = (keys, saved) =>
    Object.fromEntries(keys.map((key) => [key, Boolean(saved && saved[key])]))

const form = useForm({
    verb: null,
    review_notes: props.review.review_notes || '',
    customer_alert_list_result: props.review.customer_alert_list_result || 'not_checked',
    customer_alert_list_notes: props.review.customer_alert_list_notes || '',
    consolidated_screening_list_result: props.review.consolidated_screening_list_result || 'not_checked',
    consolidated_screening_list_notes: props.review.consolidated_screening_list_notes || '',
    site_inspection_review: seedChecklist(props.siteInspectionChecks, props.review.site_inspection_review),
    security_controls_review: seedChecklist(props.securityControlChecks, props.review.security_controls_review),
})

const submitReview = (verb) => {
    form.verb = verb
    form.patch(route('bridge.enterprise-applications.review', props.application.id), {
        preserveScroll: true,
    })
}
</script>
